"use client";

import { Translate } from "@/components/Translate";
import { Divider } from "@/components/ui/Divider";
import { formatPrice } from "@/lib/utils";
import { clsx } from "@frend-digital/ui";
import type { ComponentProps } from "react";
import styles from "./cart.module.css";
import { useCart } from "./hooks";

export const CartSummary = ({ className, ...props }: ComponentProps<"div">) => {
	const { data } = useCart();

	const campaignDiscounts =
		data?.items?.reduce(
			(acc, item) => {
				if (item.campaign) {
					const campaignName = item.campaign.name;
					const discount = item.campaign.discountAsNumber || 0;

					if (campaignName && discount > 0) {
						if (!acc[campaignName]) {
							acc[campaignName] = 0;
						}
						acc[campaignName] += discount * item?.quantity!;
					}
				}
				return acc;
			},
			{} as Record<string, number>,
		) ?? {};

	const memberDiscounts = data?.vouchers?.filter((voucher) =>
		voucher.description?.toLowerCase().includes("loyalty"),
	);

	const standardVouchers =
		data?.vouchers?.filter((voucher) => !voucher.description?.toLowerCase().includes("loyalty")) ??
		[];

	const loyaltyDiscount =
		memberDiscounts?.reduce((sum, voucher) => sum + Math.abs(voucher.priceOffAsNumber || 0), 0) ??
		0;

	const standardVouchersDiscount =
		standardVouchers?.reduce((sum, voucher) => sum + Math.abs(voucher.priceOffAsNumber || 0), 0) ?? 0;

	const automaticDiscounts =
		data?.automaticDiscounts?.filter(
			(discount) => discount.priceOffAsNumber !== 0 && discount.automaticDiscount !== "51459",
		) ?? [];

	// Calculate total discount from campaigns, automatic discounts, and standard vouchers (excluding loyalty)
	const campaignDiscountTotal = Object.values(campaignDiscounts).reduce((sum, val) => sum + val, 0);
	const automaticDiscountTotal = automaticDiscounts.reduce(
		(sum, discount) => sum + Math.abs(discount.priceOffAsNumber || 0),
		0,
	);
	const totalNonLoyaltyDiscount = campaignDiscountTotal + automaticDiscountTotal + standardVouchersDiscount;

	return (
		<div {...props} className={clsx(className, styles.summary)}>
			<div className={styles.summaryRow}>
				<span>
					<Translate>Products</Translate>
				</span>
				<span>
					{formatPrice(data?.totalBeforeDiscount)} {data?.currency}
				</span>
			</div>
			{/* Show total discount (campaigns, automatic, vouchers) excluding loyalty */}
			{totalNonLoyaltyDiscount > 0 && (
				<>
					<Divider className={styles.divider} />
					<div className={styles.summaryRow}>
						<span>
							<Translate>Discount</Translate>
						</span>
						<span className={styles.discountValue}>
							-{totalNonLoyaltyDiscount.toFixed(0)} {data?.currency}
						</span>
					</div>
				</>
			)}
			{/* Show bonus points discount separately */}
			{!!memberDiscounts?.length && (
				<>
					<Divider className={styles.divider} />
					<div className={styles.summaryRow}>
						<span>
							<Translate>Bonus points</Translate>
						</span>
						<span className={styles.discountValue}>
							-{loyaltyDiscount.toFixed(0)} {data?.currency}
						</span>
					</div>
				</>
			)}
			<Divider className={styles.divider} />
			<div className={styles.summaryRow}>
				<span>
					<Translate>Shipping</Translate>
				</span>
				<span>
					{data?.shippingPriceAsNumber ? (
						formatPrice(data.shippingPrice)
					) : (
						<Translate>Free</Translate>
					)}
				</span>
			</div>
			<Divider className={styles.divider} />
			<div className={styles.summaryTotal}>
				<span>
					<Translate>Total</Translate>
				</span>
				<span>{formatPrice(data?.total)}</span>
			</div>
		</div>
	);
};
